import React from "react";
import { Box, GlobalStyles, Typography } from "@mui/material";
import { colors, pageShellSx } from "./constants";

export function PageShell({ children }) {
  return (
    <Box sx={pageShellSx}>
      <FestiveEffects />
      <Lantern side="left" />
      <Lantern side="right" />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          textAlign: "center",
          position: "relative",
          zIndex: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function FestiveEffects() {
  return (
    <>
      <GlobalStyles
        styles={{
          "@keyframes lanternSwing": {
            "0%": { transform: "rotate(4deg) translateY(0px)" },
            "50%": { transform: "rotate(-4deg) translateY(5px)" },
            "100%": { transform: "rotate(4deg) translateY(0px)" },
          },
          "@keyframes festivePulse": {
            "0%": {
              boxShadow:
                "0 14px 30px rgba(86, 31, 2, 0.28), 0 0 0 rgba(255, 209, 102, 0.12)",
            },
            "50%": {
              boxShadow:
                "0 18px 36px rgba(86, 31, 2, 0.34), 0 0 30px rgba(255, 209, 102, 0.4)",
            },
            "100%": {
              boxShadow:
                "0 14px 30px rgba(86, 31, 2, 0.28), 0 0 0 rgba(255, 209, 102, 0.12)",
            },
          },
          "@keyframes fireworksBlink": {
            "0%": { opacity: 0.35, transform: "scale(0.95)" },
            "50%": { opacity: 0.8, transform: "scale(1.04)" },
            "100%": { opacity: 0.35, transform: "scale(0.95)" },
          },
          "@keyframes sparkleFloat": {
            "0%": { opacity: 0.25, transform: "translateY(0px) scale(0.96)" },
            "50%": { opacity: 0.75, transform: "translateY(-8px) scale(1.06)" },
            "100%": { opacity: 0.25, transform: "translateY(0px) scale(0.96)" },
          },
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {[
          { left: "10%", bottom: 40, size: 160, delay: "0s" },
          { left: "50%", bottom: 16, size: 210, delay: "0.8s" },
          { right: "10%", bottom: 54, size: 170, delay: "1.4s" },
        ].map((item) => (
          <Box
            key={JSON.stringify(item)}
            sx={{
              position: "absolute",
              ...item,
              width: item.size,
              height: item.size,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255, 209, 102, 0.28) 0%, rgba(255, 209, 102, 0.12) 32%, rgba(255, 209, 102, 0.04) 58%, transparent 74%)",
              filter: "blur(2px)",
              animation: `fireworksBlink 3.8s ease-in-out ${item.delay} infinite`,
            }}
          />
        ))}
        {[
          { left: "18%", top: "24%", delay: "0.2s" },
          { left: "32%", bottom: "22%", delay: "1.3s" },
          { right: "22%", top: "18%", delay: "0.9s" },
          { right: "28%", bottom: "20%", delay: "1.8s" },
        ].map((item, index) => (
          <Typography
            key={index}
            component="span"
            sx={{
              position: "absolute",
              ...item,
              color: colors.gold,
              fontSize: index % 2 === 0 ? "1.1rem" : "0.95rem",
              animation: `sparkleFloat 2.8s ease-in-out ${item.delay} infinite`,
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            {index % 2 === 0 ? "✦" : "✧"}
          </Typography>
        ))}
      </Box>
    </>
  );
}

function Lantern({ side }) {
  const isLeft = side === "left";
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: { xs: 30, sm: 42 },
        [isLeft ? "left" : "right"]: { xs: 8, sm: 28 },
        width: { xs: 48, sm: 62 },
        zIndex: 1,
        display: { xs: "none", sm: "block" },
        transformOrigin: isLeft ? "top right" : "top left",
        animation: `lanternSwing ${isLeft ? "3.7s" : "4.1s"} ease-in-out infinite`,
      }}
    >
      <Box
        sx={{
          width: 2,
          height: 56,
          mx: "auto",
          background:
            "linear-gradient(180deg, rgba(255, 209, 102, 0.9), rgba(255, 209, 102, 0.18))",
        }}
      />
      <Box
        sx={{
          position: "relative",
          mx: "auto",
          mt: -1,
          width: "100%",
          height: { xs: 62, sm: 78 },
          borderRadius: "40% 40% 32% 32% / 24% 24% 40% 40%",
          background: `linear-gradient(180deg, #f25b33 0%, ${colors.red} 50%, #8f041c 100%)`,
          border: `2px solid ${colors.deepGold}`,
          boxShadow:
            "0 10px 28px rgba(76, 0, 6, 0.34), inset 0 0 18px rgba(255, 209, 102, 0.24)",
          "&::before, &::after": {
            content: '""',
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            width: "72%",
            borderTop: "1px solid rgba(255, 209, 102, 0.55)",
          },
          "&::before": { top: "32%" },
          "&::after": { top: "58%" },
        }}
      >
        <Typography
          sx={{ color: colors.gold, fontSize: "1.25rem", lineHeight: "78px" }}
        >
          🏮
        </Typography>
      </Box>
      <Box
        sx={{
          width: 10,
          height: 18,
          mx: "auto",
          mt: 0.2,
          borderRadius: "0 0 10px 10px",
          background: `linear-gradient(180deg, ${colors.deepGold}, #9b5f0f)`,
        }}
      />
      <Box
        sx={{
          width: 2,
          height: 22,
          mx: "auto",
          background: `linear-gradient(180deg, ${colors.deepGold}, rgba(255, 209, 102, 0))`,
        }}
      />
    </Box>
  );
}
